import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { useMe } from "../../../swr";
import { authSession } from "../token_store";
import { authOtpService, authPasswordService } from "../../api_client";
import { AppRoutes } from "../../../common/routes/app_routes";
import { showErrorToast, showSuccessToast } from "../../../utils/toasts";
import { useRegisterWizard } from "../domain/register-wizard";
import { dashboardRouteForRole, resolvePostLoginRedirect } from "../utils/post-login-redirect";

interface UsePasswordLoginOptions {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
}

type LoginResponseShape =
    | { accessToken: string }
    | { requiresTwoFactor: true; mfaToken: string; expiresIn: number };

function isTwoFactorChallenge(
    payload: LoginResponseShape | undefined | null,
): payload is { requiresTwoFactor: true; mfaToken: string; expiresIn: number } {
    return !!payload && (payload as { requiresTwoFactor?: boolean }).requiresTwoFactor === true;
}

function isEmailNotVerified(error: unknown): boolean {
    if (!(error instanceof AxiosError) || error.response?.status !== 403) return false;
    const data = error.response.data as { code?: unknown; message?: unknown } | undefined;
    if (data?.code === "EMAIL_NOT_VERIFIED") return true;
    return typeof data?.message === "string" && data.message.toLowerCase().startsWith("email not verified");
}

export function usePasswordLogin(options: UsePasswordLoginOptions = {}) {
    const navigate = useNavigate();
    const location = useLocation();
    const { mutate } = useMe();
    const { onSuccess, onError } = options;

    const [pendingMfaToken, setPendingMfaToken] = useState<string | null>(null);

    const finalizeSession = async (accessToken: string) => {
        authSession.setAccessToken(accessToken);
        const updated = await mutate();
        onSuccess?.();
        navigate(resolvePostLoginRedirect(location, dashboardRouteForRole(updated?.role)));
    };

    const login = async (email: string, password: string) => {
        try {
            const loginResponse = await authPasswordService.passwordLogin({
                email,
                password,
            });

            const data = loginResponse.data as LoginResponseShape;
            if (isTwoFactorChallenge(data)) {
                setPendingMfaToken(data.mfaToken);
                showSuccessToast("Enter the verification code sent to your email");
                return loginResponse;
            }

            await finalizeSession(data.accessToken);
            return loginResponse;
        } catch (error: unknown) {
            if (isEmailNotVerified(error)) {
                useRegisterWizard.setState({ email, registered: true });
                try {
                    await authOtpService.otpResendVerification({ email });
                } catch {
                    // Non-fatal: user can hit "Resend" on the verify page.
                }
                showSuccessToast("Please verify your email to continue. We sent you a code.");
                navigate(AppRoutes.CREATE_ACCOUNT_VERIFY_EMAIL);
                onError?.(error as Error);
                throw error;
            }
            const errorMessage =
                (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                (error as { message?: string })?.message ||
                "Login failed";
            showErrorToast(errorMessage);
            onError?.(error as Error);
            throw error;
        }
    };

    const verifyTwoFactor = async (code: string) => {
        if (!pendingMfaToken) return;
        try {
            const res = await authPasswordService.twoFactorVerifyLogin({
                mfaToken: pendingMfaToken,
                code,
            });
            setPendingMfaToken(null);
            await finalizeSession(res.data.accessToken);
        } catch (error: unknown) {
            const errorMessage =
                (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                "Invalid or expired code";
            showErrorToast(errorMessage);
            throw error;
        }
    };

    const cancelTwoFactor = () => setPendingMfaToken(null);

    return { login, verifyTwoFactor, cancelTwoFactor, pendingMfaToken };
}

